import type React from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { selectEntities } from '@polyai/stream/store/reducers/entities/selectors';
import type { Step } from '@polyai/stream/store/reducers/flows';

import { useAppSelector } from 'hooks/reduxHooks';
import { getUniqueEntityName } from 'screens/FlowDetails/helpers';
import EntitySidebar from '../components/EntitySidebar';
import useFlowDetailsStore from '../hooks/useFlowDetailsStore';

type OnUpsert = (entity: { id: string; name: string }) => void;

type OnCancelUpsert = () => void;

type UpsertRef = {
  onUpsert: OnUpsert;
  onCancelUpsert: OnCancelUpsert;
};

interface EntitySidebarContextType {
  openEntitySidebar: (options: {
    stepId?: string;
    defaultName?: string;
    entityId?: string;
    stepType: Step['type'];
    upsertRef?: UpsertRef;
  }) => void;
  closeEntitySidebar: () => void;
  setEntityId: Dispatch<SetStateAction<string>>;
  isOpen: boolean;
  stepId: string;
  defaultName: string;
  entityId: string;
  stepType: Step['type'] | undefined;
  upsertRef: MutableRefObject<UpsertRef | undefined>;
}

const EntitySidebarContext = createContext<EntitySidebarContextType | null>(
  null,
);

export const useEntitySidebar = () => {
  const context = useContext(EntitySidebarContext);
  if (!context) {
    throw new Error(
      'useEntitySidebar must be used within an EntitySidebarProvider',
    );
  }
  return context;
};

interface EntitySidebarProviderProps {
  readonly: boolean;
  children: React.ReactNode;
}

export const EntitySidebarProvider: React.FC<EntitySidebarProviderProps> = ({
  readonly,
  children,
}) => {
  const [stepId, setStepId] = useState<string>('');
  const [defaultName, setDefaultName] = useState<string>('');
  const [entityId, setEntityId] = useState<string>('');
  const [stepType, setStepType] = useState<Step['type'] | undefined>(undefined);
  const upsertRef = useRef<UpsertRef>(undefined);

  const entities = useAppSelector(selectEntities);

  const { setActiveSidebar, isSidebarOpen } = useFlowDetailsStore();

  const openEntitySidebar: EntitySidebarContextType['openEntitySidebar'] =
    useCallback(
      options => {
        const existingNames = entities.map(entity => entity.name);
        setStepId(options.stepId || '');
        setDefaultName(
          options.defaultName || getUniqueEntityName(existingNames),
        );
        setEntityId(options.entityId || '');
        setStepType(options.stepType || undefined);
        setActiveSidebar({ type: 'entity' });
        upsertRef.current = options.upsertRef;
      },
      [entities, setActiveSidebar],
    );

  const closeEntitySidebar = useCallback(() => {
    setStepId('');
    setDefaultName('');
    setEntityId('');
    setStepType(undefined);
    setActiveSidebar(undefined);
  }, [setActiveSidebar]);

  const isOpen = isSidebarOpen('entity');

  return (
    <EntitySidebarContext.Provider
      value={{
        openEntitySidebar,
        closeEntitySidebar,
        setEntityId,
        isOpen,
        stepId,
        stepType,
        defaultName,
        entityId,
        upsertRef,
      }}
    >
      {children}
      <EntitySidebar
        defaultName={defaultName}
        entityId={entityId}
        isOpen={isOpen}
        readonly={readonly}
        stepId={stepId}
        stepType={stepType}
        onClose={closeEntitySidebar}
      />
    </EntitySidebarContext.Provider>
  );
};
